import { useCallback, useEffect, useMemo, useState } from 'react';

import { debounce } from '@utils/common';

import { HALF_SECOND } from '@constants/common';

interface UseDragAndDropStateOptions<T> {
	allData: T[];
	currentPageData: T[];
	page: number;
	pageSize: number;
	storageKey: string;
	getItemId: (item: T, index: number) => string;
	isEnabled?: boolean;
}

interface UseDragAndDropStateReturn<T> {
	reorderedData: T[];
	onRowReorder: (oldIndex: number, newIndex: number) => void;
	resetOrder: () => void;
	hasCustomOrder: boolean;
}

export function useDragAndDropState<T>({
	allData,
	currentPageData,
	page,
	pageSize,
	storageKey,
	getItemId,
	isEnabled = true,
}: UseDragAndDropStateOptions<T>): UseDragAndDropStateReturn<T> {
	const [globalOrder, setGlobalOrder] = useState<string[]>([]);

	useEffect(() => {
		if (!isEnabled) {
			return;
		}

		const savedOrder = localStorage.getItem(storageKey);

		if (savedOrder) {
			const parsedOrder = JSON.parse(savedOrder);

			if (Array.isArray(parsedOrder)) {
				setGlobalOrder(parsedOrder);
			}
		}
	}, [storageKey, isEnabled]);

	const debouncedSave = useMemo(
		() => debounce((order: string[], key: string) => {
			if (order.length > 0) {
				localStorage.setItem(key, JSON.stringify(order));
			} else {
				localStorage.removeItem(key);
			}
		}, HALF_SECOND), []);

	useEffect(() => {
		if (!isEnabled) {
			return;
		}

		debouncedSave(globalOrder, storageKey);
	}, [globalOrder, storageKey, isEnabled, debouncedSave]);

	const itemIdCache = useMemo(() => {
		const cache = new Map<T, string>();
		const workingData = allData?.length > 0 ? allData : currentPageData;

		workingData?.forEach((item, index) => {
			cache.set(item, getItemId(item, index));
		});

		return cache;
	}, [allData, currentPageData, getItemId]);

	const globallyOrderedData = useMemo(() => {
		if (!isEnabled) {
			return allData || [];
		}

		const workingData = allData?.length > 0 ? allData : currentPageData;

		if (!workingData?.length) {
			return [];
		}

		if (globalOrder.length === 0) {
			return workingData;
		}

		const itemMap = new Map<string, T>();
		const orderSet = new Set(globalOrder);
		const orderedItems: T[] = [];
		const unorderedItems: T[] = [];

		workingData.forEach(item => {
			const id = itemIdCache.get(item) || getItemId(item, 0);

			itemMap.set(id, item);

			if (!orderSet.has(id)) {
				unorderedItems.push(item);
			}
		});

		globalOrder.forEach(id => {
			const item = itemMap.get(id);

			if (item) {
				orderedItems.push(item);
			}
		});

		return [...orderedItems, ...unorderedItems];
	}, [allData, currentPageData, globalOrder, itemIdCache, isEnabled, getItemId]);

	useEffect(() => {
		if (!isEnabled || !allData?.length || globalOrder.length === 0) {
			return;
		}

		const currentIds = allData.map((item, index) => getItemId(item, index));
		const currentIdsSet = new Set(currentIds);
		const existingIds = new Set(globalOrder);
		const newIds = currentIds.filter(id => !existingIds.has(id));

		if (newIds.length > 0) {
			setGlobalOrder(prev => {
				const validIds = prev.filter(id => currentIdsSet.has(id));

				return [...validIds, ...newIds];
			});
		} else {
			const validIds = globalOrder.filter(id => currentIdsSet.has(id));

			if (validIds.length !== globalOrder.length) {
				setGlobalOrder(validIds);
			}
		}
	}, [allData, globalOrder, getItemId, isEnabled]);

	const pageIndices = useMemo(() => ({
		start: (page - 1) * pageSize,
		end: page * pageSize,
	}), [page, pageSize]);

	const reorderedData = useMemo(() => {
		if (!globallyOrderedData.length) {
			return currentPageData;
		}

		return globallyOrderedData.slice(pageIndices.start, pageIndices.end);
	}, [globallyOrderedData, pageIndices, currentPageData]);

	const onRowReorder = useCallback((oldIndex: number, newIndex: number) => {
		if (!isEnabled || oldIndex === newIndex) {
			return;
		}

		const workingData = globallyOrderedData.length > 0
			? globallyOrderedData
			: currentPageData;

		if (!workingData.length) {
			return;
		}

		const isGlobalReorder = workingData === globallyOrderedData;

		const actualOldIndex = isGlobalReorder
			? pageIndices.start + oldIndex
			: oldIndex;

		const actualNewIndex = isGlobalReorder
			? pageIndices.start + newIndex
			: newIndex;

		if (
			actualOldIndex < 0 ||
			actualOldIndex >= workingData.length ||
			actualNewIndex < 0 ||
			actualNewIndex >= workingData.length
		) {
			return;
		}

		const movedItem = workingData[actualOldIndex];
		const targetItem = workingData[actualNewIndex];

		if (!movedItem || !targetItem) {
			return;
		}

		const movedItemId = itemIdCache.get(movedItem) || getItemId(movedItem, 0);
		const targetItemId = itemIdCache.get(targetItem) || getItemId(targetItem, 0);

		const currentOrder = globalOrder.length > 0
			? [...globalOrder]
			: workingData.map(item => itemIdCache.get(item) || getItemId(item, 0));

		const orderIndexMap = new Map<string, number>();

		currentOrder.forEach((id, index) => orderIndexMap.set(id, index));

		const movedItemIndex = orderIndexMap.get(movedItemId);
		const targetItemIndex = orderIndexMap.get(targetItemId);

		if (movedItemIndex === undefined || targetItemIndex === undefined) {
			return;
		}

		currentOrder.splice(movedItemIndex, 1);
		currentOrder.splice(targetItemIndex, 0, movedItemId);

		setGlobalOrder(currentOrder);
	}, [
		isEnabled,
		globallyOrderedData,
		currentPageData,
		pageIndices,
		globalOrder,
		itemIdCache,
		getItemId,
	]);

	const resetOrder = useCallback(() => {
		debouncedSave.cancel();
		setGlobalOrder([]);
		localStorage.removeItem(storageKey);
	}, [storageKey, debouncedSave]);

	useEffect(() => {
		return () => {
			debouncedSave.cancel();
		};
	}, [debouncedSave]);

	const hasCustomOrder = globalOrder.length > 0;

	return {
		reorderedData,
		onRowReorder,
		resetOrder,
		hasCustomOrder,
	};
}
